import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "../contexts/AuthContext";
import apiService from "../services/api";

const HEARTBEAT_MS = 2000;

/**
 * Tracks which admins are currently viewing a ticket.
 *
 * Heartbeats every 2s while the tab is visible; on hidden/pagehide we
 * fire a best-effort beacon and stop polling. The server enforces a 5s
 * TTL so we don't need authenticated cleanup on tab close — normal
 * unmount/route changes still send an authenticated DELETE.
 *
 * The first heartbeat does NOT fire onJoin for already-present admins;
 * they were already there when we started watching.
 *
 * @param {string|number} ticketId
 * @param {{ onJoin?: (viewer) => void, onLeave?: (viewer) => void }} [callbacks]
 * @returns {{ viewers: Array, setTyping: (boolean) => void }}
 */
export const useTicketPresence = (ticketId, { onJoin, onLeave } = {}) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [viewers, setViewers] = useState([]);

  const isTypingRef = useRef(false);
  const intervalRef = useRef(null);
  const aliveRef = useRef(true);
  const previousViewersRef = useRef([]);
  const firstRenderRef = useRef(true);
  const onJoinRef = useRef(onJoin);
  const onLeaveRef = useRef(onLeave);

  // Keep callback refs current without re-subscribing the polling effect.
  useEffect(() => {
    onJoinRef.current = onJoin;
    onLeaveRef.current = onLeave;
  }, [onJoin, onLeave]);

  const sendHeartbeat = useCallback(async () => {
    if (!aliveRef.current || !ticketId) return;
    try {
      const res = await apiService.heartbeatTicketPresence(
        ticketId,
        isTypingRef.current
      );

      if (!aliveRef.current) return;

      const next = Array.isArray(res?.viewers) ? res.viewers : [];

      if (firstRenderRef.current) {
        firstRenderRef.current = false;
      } else {
        const prev = previousViewersRef.current;
        const prevIds = new Set(prev.map((v) => v.adminId));
        const nextIds = new Set(next.map((v) => v.adminId));

        next.forEach((v) => {
          if (!prevIds.has(v.adminId)) onJoinRef.current?.(v);
        });
        prev.forEach((v) => {
          if (!nextIds.has(v.adminId)) onLeaveRef.current?.(v);
        });
      }

      previousViewersRef.current = next;
      setViewers(next);
    } catch {
      // Silent retry on the next tick — banner stays in last-known state.
    }
  }, [ticketId]);

  const startHeartbeat = useCallback(() => {
    if (intervalRef.current) return;
    sendHeartbeat();
    intervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_MS);
  }, [sendHeartbeat]);

  const stopHeartbeat = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const sendBeaconLeave = useCallback(() => {
    if (!ticketId) return;
    try {
      const base = process.env.REACT_APP_API_URL || "";
      const url = `${base}/v1/dashboard/tickets/${ticketId}/presence?leave=1`;

      if (typeof navigator !== "undefined" && navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([], { type: "application/json" }));
      }
    } catch {
      // best-effort — TTL cleans up within 5s
    }
  }, [ticketId]);

  useEffect(() => {
    if (!isAdmin || !ticketId) return undefined;

    aliveRef.current = true;
    firstRenderRef.current = true;
    previousViewersRef.current = [];
    startHeartbeat();

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        stopHeartbeat();
        sendBeaconLeave();
      } else if (document.visibilityState === "visible") {
        startHeartbeat();
      }
    };

    const onPageHide = () => {
      sendBeaconLeave();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);

    return () => {
      aliveRef.current = false;
      stopHeartbeat();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
      apiService.leaveTicketPresence(ticketId).catch(() => {});
    };
  }, [isAdmin, ticketId, startHeartbeat, stopHeartbeat, sendBeaconLeave]);

  const setTyping = useCallback((value) => {
    isTypingRef.current = !!value;
  }, []);

  return { viewers: isAdmin ? viewers : [], setTyping };
};

export default useTicketPresence;
